import { useEffect, useState } from "react";
import api from "../lib/api";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import NearbyTasks from "../components/NearbyTasks";
import { toast } from "sonner";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    toast.success("All notifications marked as read");
    load();
  };

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    load();
  };

  const unread = items.filter((i) => !i.read).length;

  return (
    <div className="px-4 sm:px-8 py-8 max-w-3xl mx-auto">
      <div className="flex items-end justify-between mb-7 anim-fade-up">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Inbox</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-2">{unread} unread · {items.length} total</p>
        </div>
        {items.length > 0 && (
          <Button variant="outline" onClick={markAll} data-testid="notif-mark-all-btn">
            <CheckCheck size={14} className="mr-1.5" />
            Mark all read
          </Button>
        )}
      </div>

      <NearbyTasks />

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/40 p-12 text-center">
          <Bell size={28} className="mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
          <h3 className="font-display font-bold">You're all caught up</h3>
          <p className="text-sm text-muted-foreground mt-1">Create tasks with due dates to get reminders here.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((n) => (
            <li
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              className={`flex items-start gap-3 p-4 rounded-lg border ${n.read ? "border-border bg-card" : "border-primary/30 bg-primary/5"}`}
            >
              <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${n.read ? "bg-muted-foreground/40" : "bg-primary"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{n.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!n.read && (
                  <Button size="sm" variant="ghost" onClick={() => markOne(n.id)} data-testid={`notif-read-${n.id}`}>
                    <CheckCheck size={13} />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => remove(n.id)} data-testid={`notif-delete-${n.id}`}>
                  <Trash2 size={13} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
